'use client';

import InfoCard from '@/components/infoCard/infoCard';
import { useTranslation } from '@/components/translation';
import { useShowSuccessAlert } from '@/hooks/useShowSuccessAlert';
import { CommPrefChannel, CommPrefObj } from '@/lib/types/datatypes';
import useCommPrefsQuery from '@/hooks/useCommPrefsQuery';
import PreferenceStatusTag from '@/components/preference-status-tag/preference-status-tag';
import PreferenceStatusTagGroup from '@/components/preference-status-tag-group/preference-status-tag-group';
import Layout from '@/components/layout/layout';
import PreferenceTile from '@/components/preference-tile/preference-tile';
import {
  EMAILS_URL,
  EMAIL_ALERTS_URL,
  PHARMACY_EMAILS_URL,
  EXTRACARE_EMAILS_URL,
  SPECIALTY_EMAIL_URL,
  CAREMARK_EMAIL_URL,
  H100_EMAILS_URL,
} from '@/lib/constants';
import { getSpecialtyCurrentPref, getCaremarkPrefs, isHealth100Client } from '@/lib/utils/utils';
import styles from '@/app/communication-preferences/communication-preferences.module.scss';
import { useEffect } from 'react';
import { triggerViewTag } from '@/lib/utils/adobeTagging';

const EmailsPage = () => {
  const { isFetching, isLoading, data, isError } = useCommPrefsQuery();

  // i18n translations
  const pageTitle = useTranslation('page.emails.title');
  const cvsEmailsLabel = useTranslation('emails.cvs.label');
  const pharmacyEmailsLabel = useTranslation('emails.pharmacy.label');
  const extracareEmailsLabel = useTranslation('emails.extracare.label');
  const specialtyEmailsLabel = useTranslation('emails.specialty.label');
  const caremarkEmailsLabel = useTranslation('emails.caremark.label');
  const h100EmailsLabel = useTranslation('emails.health100.label');
  const ecOffersLabel = useTranslation('extracare.emails.title');
  const instoreReceiptsLabel = useTranslation('extracare.emails.instoreReceiptsHeading');
  const comingSoonTitle = useTranslation('comingSoon.title');
  const comingSoonDescription = useTranslation('comingSoon.description');

  const specialtyData = data?.data?.linkedSSKs;
  const spltyCommPrefs = specialtyData?.find(
    (prefObj) => prefObj?.preferencesInfo?.preferences?.[0]?.preferenceType === 'SPECIALTY_PREFERENCE',
  );
  const isSpecialtyTied = !isError && specialtyData && spltyCommPrefs;
  const subscriptionOption = getSpecialtyCurrentPref(
    spltyCommPrefs?.preferencesInfo?.preferences?.[0]?.programs,
    'EMAIL',
  );
  const cmkPref = data?.data?.linkedSSKs?.filter(
    (ssk) =>
      ssk.preferencesInfo?.preferences?.[0]?.preferenceType === 'CAREMARK_PREFERENCE' &&
      ssk.preferencesInfo?.statusCode === '0000',
  );
  const isCmkTied = cmkPref?.length === 1; // mark user as untied if zero or multiple QLIDs are found
  const cmkPrograms = cmkPref?.[0]?.preferencesInfo?.preferences?.[0]?.programs || [];
  const { [2]: cmkEmailEnrolledStatus } = getCaremarkPrefs('EMAIL', cmkPrograms);

  useShowSuccessAlert(EMAILS_URL);

  const isRxTied = data?.data?.profile?.rxTied === 'Y';
  const isEcPrimary = data?.data?.profile?.extraCareDetails?.extracareAccessLevel === 'P';

  // yes, this is a little ridiculous, but the data structure is convoluted
  const cvsEmailEnrolled =
    data?.data?.preferences?.preferences?.[0]?.programs?.[0]?.channels?.find((channel) => channel.system === 'EMAIL')
      ?.subscriptionStatus === 'Y';
  const rxCommPref = data?.data?.preferences?.preferences?.find(
    (pref: CommPrefObj) => pref.preferenceType === 'PHARMACY_PREFERENCE',
  );
  const rxEmailEnrolled =
    rxCommPref?.programs?.[0]?.channels?.find((channel: CommPrefChannel) => channel.system === 'EMAIL')
      ?.subscriptionStatus === 'IN';
  const eCEmail = data?.data?.profile?.extraCareDetails?.ecProfileEmail || '';
  const digitalReceiptEnrolled = ['B', 'D'].includes(data?.data?.profile?.extraCareDetails?.digitalReceiptPref || '');
  const ecEmailEnrolled = !!data?.data?.profile?.extraCareDetails?.emailOptFlag;
  const h100EmailEnrolled = isHealth100Client()
    ? data?.data?.preferences?.preferences?.find((preference) => preference.preferenceType === 'H100_PREFERENCES')
        ?.contactPoints?.[0]?.status === 'IN'
    : false;
  const hasH100Email = isHealth100Client()
    && data?.data?.preferences?.preferences?.find((preference) => preference.preferenceType === 'H100_PREFERENCES')
        ?.contactPoints?.[0]?.value;

  useEffect(() => {
    void triggerViewTag({
      pageName: 'account:my_account:communication_preferences:emails',
      pageCategory: 'account',
    });
  }, []);

  return (
    <Layout loading={isFetching || isLoading} title={pageTitle}>
      {!isFetching && !isLoading && (
        <div className={styles.preferenceTilesContainer}>
          {!isFetching && !isRxTied && !isEcPrimary && !isCmkTied && isHealth100Client() ? (
            <InfoCard title={comingSoonTitle} description={comingSoonDescription} />
          ) : (
            <ul className={styles.prefTileUlBullets}>
              {/* TODO Update tile routes once the target pages are built */}
              {isHealth100Client() && hasH100Email && (
                <li>
                  <PreferenceTile
                    label={h100EmailsLabel}
                    route={H100_EMAILS_URL}
                    description={<PreferenceStatusTag enrolledStatus={h100EmailEnrolled} />}
                    linkTag="account:my_account:communication_preferences:h100_emails"
                  />
                </li>
              )}
              {!isHealth100Client() && (
                <li>
                  <PreferenceTile
                    label={cvsEmailsLabel}
                    route={EMAIL_ALERTS_URL}
                    description={<PreferenceStatusTag enrolledStatus={cvsEmailEnrolled} />}
                    linkTag="account:my_account:communication_preferences:emails:cvs_deals"
                  />
                </li>
              )}
              {isRxTied && (
                <li>
                  <PreferenceTile
                    label={pharmacyEmailsLabel}
                    route={PHARMACY_EMAILS_URL}
                    description={<PreferenceStatusTag enrolledStatus={rxEmailEnrolled} />}
                    linkTag="account:my_account:communication_preferences:emails:pharmacy_emails"
                  />
                </li>
              )}
              {!isHealth100Client() && isEcPrimary && (
                <li>
                  <PreferenceTile
                    label={extracareEmailsLabel}
                    route={EXTRACARE_EMAILS_URL}
                    linkTag="account:my_account:communication_preferences:emails:extracare_offers_and_in_store_receipts"
                    description={
                      <PreferenceStatusTagGroup>
                        <PreferenceStatusTag
                          enrolledStatus={Boolean(eCEmail && ecEmailEnrolled)}
                          label={ecOffersLabel}
                        />
                        <PreferenceStatusTag
                          enrolledStatus={Boolean(eCEmail && digitalReceiptEnrolled)}
                          label={instoreReceiptsLabel}
                        />
                      </PreferenceStatusTagGroup>
                    }
                  />
                </li>
              )}
              {!isFetching && isSpecialtyTied && (
                <li>
                  <PreferenceTile
                    label={specialtyEmailsLabel}
                    route={SPECIALTY_EMAIL_URL}
                    description={
                      <PreferenceStatusTag enrolledStatus={Boolean(isSpecialtyTied && subscriptionOption === 'IN')} />
                    }
                    linkTag="account:my_account:communication_preferences:emails:specialty"
                  />
                </li>
              )}
              {!isFetching && isCmkTied && (
                <li>
                  <PreferenceTile
                    label={caremarkEmailsLabel}
                    route={CAREMARK_EMAIL_URL}
                    description={
                      <PreferenceStatusTag enrolledStatus={Boolean(isCmkTied && cmkEmailEnrolledStatus === 'IN')} />
                    }
                    linkTag="account:my_account:communication_preferences:emails:caremark"
                  />
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </Layout>
  );
};

export default EmailsPage;
